import React from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import {
  getWholesalerDayTransactionColumns,
  wholesalerWeeklyColumns,
  wholesalerMonthColumns,
  TIME_FRAMES,
} from "./columns";
import { format, parseISO } from "date-fns";
import { reportAdminModalStyles } from "../../components/reportStyles";
import { getFormattedWeek } from "./Excel";

const WholesalerDetailsModal = ({
  open,
  onClose,
  wholesalerData,
  currentTimeFrame,
}) => {
  if (!wholesalerData) return null;

  let reportData = [];

  if (currentTimeFrame === TIME_FRAMES.DAY) {
    reportData = wholesalerData.dayWiseReport || [];
  } else if (currentTimeFrame === TIME_FRAMES.WEEK) {
    reportData = wholesalerData.weekWiseReport || [];
  } else if (currentTimeFrame === TIME_FRAMES.MONTH) {
    reportData = wholesalerData.monthWiseReport || [];
  }
  const columns =
    currentTimeFrame === TIME_FRAMES.DAY
      ? getWholesalerDayTransactionColumns
      : currentTimeFrame === TIME_FRAMES.WEEK
      ? wholesalerWeeklyColumns
      : wholesalerMonthColumns;

  const getTimeFrameText = () => {
    switch (currentTimeFrame) {
      case TIME_FRAMES.DAY:
        return "Daily";
      case TIME_FRAMES.WEEK:
        return "Weekly";
      case TIME_FRAMES.MONTH:
        return "Monthly";
      default:
        return "";
    }
  };

  const wholesalerName = wholesalerData.Name || "N/A";

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box sx={reportAdminModalStyles.wholesalerModalStyle}>
        {/* Header */}
        <Box sx={reportAdminModalStyles.wholesalerHeaderBox}>
          <Box sx={reportAdminModalStyles.wholesalerHeaderBoxFlex}>
            <Typography
              variant="h5"
              fontWeight={600}
              color="#06214b"
              sx={reportAdminModalStyles.wholesalerHeaderBoxLine}
            >
              {getTimeFrameText()} Wholesaler Transactions
            </Typography>

            <Box sx={reportAdminModalStyles.headerBoxDisplay}>
              <CorporateFareIcon sx={reportAdminModalStyles.iconStyle} />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={reportAdminModalStyles.headerBoxTextTransform}
              >
                <Chip
                  label={wholesalerName}
                  sx={reportAdminModalStyles.chipStyle}
                  title={wholesalerName}
                />{" "}
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={onClose} sx={reportAdminModalStyles.closeIcon}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={reportAdminModalStyles.contentBoxStyle}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={reportAdminModalStyles.tableContainer}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      sx={reportAdminModalStyles.tableStyle}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {reportData.length > 0 ? (
                  reportData.map((report, index) => (
                    <TableRow
                      key={index}
                      sx={reportAdminModalStyles.tableBodyRow}
                    >
                      {currentTimeFrame === TIME_FRAMES.MONTH && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {report.month
                              ? format(
                                  parseISO(`${report.month}-01`),
                                  "MMMM yyyy"
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellMonth}
                            >
                              {(report.monthlyTransactions ?? 0).toFixed(2)}
                            </Box>
                          </TableCell>
                        </>
                      )}

                      {currentTimeFrame === TIME_FRAMES.WEEK && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {getFormattedWeek(report.week)}
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellWeek}
                            >
                              {(report.weeklyTransactions ?? 0).toFixed(2)}
                            </Box>
                          </TableCell>
                        </>
                      )}

                      {currentTimeFrame === TIME_FRAMES.DAY && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {report.date
                              ? format(parseISO(report.date), "dd MMM yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {report.mostOrderedBrand || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellBrand}
                            >
                              {(report.transactions ?? 0).toFixed(2)}
                            </Box>
                          </TableCell>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {report.totalBottlesSold || 0}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={reportAdminModalStyles.tableCellStyles}
                    >
                      No transaction data available for this{" "}
                      {getTimeFrameText().toLowerCase()} period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Modal>
  );
};

export default WholesalerDetailsModal;
